/** @mui/material imports */
/** @mui/icons-material imports */
/** React imports */
import {
  Dispatch,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from 'react';
/** App imports */
import { appContext } from '../Contexts/AppContext';
import {
  RegisteredImageTypeCount,
  RegisteredImageTypeCountUnique,
} from '../TypeScript/interfaces';
import { RegisteredImageType } from '../TypeScript/enums';
import { ImageObject } from '@goldspotdiscoveries/types';

/**
 * The order of image types for sorting
 */
const imagesTypeOrder: string[] = [
  RegisteredImageType.wet,
  RegisteredImageType.dry,
  RegisteredImageType.atv,
  RegisteredImageType.otv,
  RegisteredImageType.core,
  RegisteredImageType.rc,
  RegisteredImageType.hs,
];

/**
 * Get a unique key for a ImageObject
 */
export const getImageObjectUniqueKey = (imageObject: ImageObject) => {
  if (imageObject.imageType == null) {
    return undefined;
  }

  let key = imageObject.imageType as string;

  if (imageObject.wavelength != null) {
    key += `|${imageObject.wavelength}`;
  }

  return key;
};

/**
 * Get a unique key for a RegisteredImageTypeCount object
 * @param imageTypeCount
 * @returns a unique key that is a concatenation of the image type and wavelength properties
 */
export const getImageTypeCountUniqueKey = (
  imageTypeCount: RegisteredImageTypeCount,
): string => {
  return `${imageTypeCount.image_type}|${imageTypeCount.wavelength || null}`;
};

/**
 * From a concatenated hyperspectral image type name, create a RegisteredImageTypeCount object
 */
export const getDestructuredImageType = (
  imageType: string,
): RegisteredImageTypeCount => {
  if (imageType?.includes('|')) {
    const [image_type, wavelength] = imageType.split('|');
    if (wavelength === 'null') {
      return { image_type, count: 0 };
    }
    return { image_type, wavelength, count: 0 };
  } else {
    return { image_type: imageType, count: 0 };
  }
};

/**
 * Sort image types in order of {@link imagesTypeOrder} and any hyperspectral image types sorted by count descending
 */
export const sortRegisteredImagesTypeList = (
  a: RegisteredImageTypeCount,
  b: RegisteredImageTypeCount,
): number => {
  const indexA: number = imagesTypeOrder.indexOf(a.image_type);
  const indexB: number = imagesTypeOrder.indexOf(b.image_type);
  if (indexA !== indexB) {
    return indexA - indexB;
  } else if (a.image_type === RegisteredImageType.hs) {
    return b.count - a.count;
  } else {
    return 0;
  }
};

/**
 * A hook to use a stateful list of image types in a project
 * @param initialValues list of registered image types
 * @returns {[RegisteredImageTypeCountUnique[], Dispatch<SetStateAction<RegisteredImageTypeCountUnique[]>>]}
 */
export const useRegisteredImageTypes = (
  initialValues: string[] = [],
): [
  RegisteredImageTypeCountUnique[],
  Dispatch<SetStateAction<RegisteredImageTypeCountUnique[]>>,
] => {
  const { activeProject } = useContext(appContext);
  const [registeredImageTypes, setRegisteredImageTypes] = useState<
    RegisteredImageTypeCountUnique[]
  >([]);

  /**
   * Update the stateful list of registered image types by adding any new items and updating the quantities of old ones
   * @param values a list of image types
   */
  const updateRegisteredImageTypes: Dispatch<
    SetStateAction<RegisteredImageTypeCountUnique[]>
  > = (values: SetStateAction<RegisteredImageTypeCountUnique[]>): void => {
    const updatedRegisteredImageTypes: SetStateAction<
      RegisteredImageTypeCountUnique[]
    > = mergeImageTypeLists(
      registeredImageTypes,
      values as RegisteredImageTypeCountUnique[],
    );
    setRegisteredImageTypes(updatedRegisteredImageTypes);
  };

  useEffect(() => {
    const initialImageTypes: RegisteredImageTypeCountUnique[] =
      initialValues.map((value: string) => {
        const imageTypeCount: RegisteredImageTypeCount = {
          image_type: value,
          count: 0,
        };
        const uniqueKey: string = getImageTypeCountUniqueKey(imageTypeCount);
        return {
          image_type: value,
          count: 0,
          uniqueKey,
        };
      });
    if (
      activeProject?.litholensData?.imagesTypeCount &&
      Array.isArray(activeProject?.litholensData?.imagesTypeCount)
    ) {
      const imagesTypeCount: RegisteredImageTypeCount[] =
        activeProject?.litholensData?.imagesTypeCount;
      const currentValues: RegisteredImageTypeCountUnique[] =
        mergeImageTypeLists(initialImageTypes, imagesTypeCount);
      setRegisteredImageTypes(currentValues);
    } else {
      setRegisteredImageTypes(initialImageTypes);
    }
  }, [activeProject]);

  return [registeredImageTypes, updateRegisteredImageTypes];
};

/**
 * Convert a list of registered image types to a map of registered image types by a key made up of the image_type name and wavelength (or 'null')
 */
const imagesTypeListToMap = (
  imagesTypeList: RegisteredImageTypeCount[],
): Record<string, RegisteredImageTypeCount> => {
  const imagesTypeMap: Record<
    RegisteredImageType | string,
    RegisteredImageTypeCount
  > = {};
  imagesTypeList.forEach((imagesType: RegisteredImageTypeCount) => {
    const uniqueKey: string = getImageTypeCountUniqueKey(imagesType);
    imagesTypeMap[uniqueKey] = imagesType;
  });
  return imagesTypeMap;
};

/**
 * Merge two lists of registered image types by:
 * 1. add new items to the current list, and
 * 2. update the counts in the current list from the new list
 * @returns the merged list
 */
const mergeImageTypeLists = (
  currentValues: RegisteredImageTypeCountUnique[] | RegisteredImageTypeCount[],
  newValues: RegisteredImageTypeCount[],
): RegisteredImageTypeCountUnique[] => {
  const imagesTypeMap: Record<
    RegisteredImageType | string,
    RegisteredImageTypeCount
  > = imagesTypeListToMap(newValues);
  const updatedRegisteredImageTypes: RegisteredImageTypeCountUnique[] = [];
  currentValues.forEach((registeredImageType: RegisteredImageTypeCount) => {
    const uniqueKey: string = getImageTypeCountUniqueKey(registeredImageType);
    if (imagesTypeMap.hasOwnProperty(uniqueKey)) {
      updatedRegisteredImageTypes.push({
        ...imagesTypeMap[uniqueKey],
        uniqueKey,
      });
      delete imagesTypeMap[uniqueKey];
    } else {
      updatedRegisteredImageTypes.push({
        ...registeredImageType,
        uniqueKey,
      });
    }
  });
  Object.keys(imagesTypeMap).forEach((key: string) => {
    const uniqueKey: string = getImageTypeCountUniqueKey(imagesTypeMap[key]);
    updatedRegisteredImageTypes.push({
      ...imagesTypeMap[key],
      uniqueKey,
    });
  });
  updatedRegisteredImageTypes.sort(sortRegisteredImagesTypeList);
  return updatedRegisteredImageTypes;
};
